import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../services/authService";
import { getFcmToken } from "../../utils/connectStore";
import toast from "../../utils/toast";
import {
  clearClientStorage,
  ACTIVITY_EVENTS,
  INACTIVITY_TIMEOUT_MS,
} from "../../utils/sessionManager";

/**
 * Watches user activity and automatically logs out the session when the user
 * has been idle for INACTIVITY_TIMEOUT_MS. This prevents abandoned sessions
 * from being hijacked and mirrors server-side token expiry.
 */
const SessionTimeoutGuard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const isLoggedIn = useSelector(
    (state) => Boolean(state?.authReducer?.tokenData?.token)
  );

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const performLogout = useCallback(async () => {
    clearTimer();
    try {
      await dispatch(logout({ fcmToken: getFcmToken() })).unwrap();
      toast.info("You were logged out due to inactivity.");
    } catch (error) {
      console.warn("[SessionTimeoutGuard] Auto logout failed:", error?.message || error);
    } finally {
      clearClientStorage();
      navigate("/", { replace: true });
    }
  }, [clearTimer, dispatch, navigate]);

  const startTimer = useCallback(() => {
    clearTimer();
    if (!isLoggedIn) return;
    timerRef.current = setTimeout(performLogout, INACTIVITY_TIMEOUT_MS);
  }, [clearTimer, isLoggedIn, performLogout]);

  useEffect(() => {
    if (!isLoggedIn) {
      clearTimer();
      return undefined;
    }

    startTimer();
    const resetTimer = () => startTimer();
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, resetTimer, { passive: true })
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
            window.removeEventListener(event, resetTimer));
      clearTimer();
    };
  }, [isLoggedIn, startTimer, clearTimer]);

  return null;
};

export default SessionTimeoutGuard;

